import { Fragment, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate, Translate } from 'react-i18nify';
import { classnames } from '../../utils';
import SkeletonComponent from '../SkeletonComponent';
import styles from './BMSLabelling.module.scss';
import useVersion from './useBmsVersion';
import ceSymbol from '../../assets/symbols/ce1912.png';
import udiSymbol from '../../assets/symbols/udi.png';
import eifuSymbol from '../../assets/symbols/eifu.png';
import manufacturerSymbol from '../../assets/symbols/manufacturer.png';
import medicalDeviceSymbol from '../../assets/symbols/md.png';

export default function BMSLabelling() {
  const { data, isLoading, error } = useVersion();

  const groupedModules: Record<BMSModuleType, BMSModule[]> = useMemo(() => {
    if (!data?.modules) return {} as Record<BMSModuleType, BMSModule[]>;

    return data.modules.reduce(
      (acc, module) => {
        const type = module.moduleType;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(module);
        return acc;
      },
      {} as Record<BMSModuleType, BMSModule[]>
    );
  }, [data?.modules]);

  const getModuleTypeKey = (moduleType: BMSModuleType) => {
    return moduleType === 'ADMINISTRATIVE_MODULE' ? 'administrative_module' : 'medical_device_module';
  };

  return (
    <div className="pt-20">
      <div className={classnames([styles.wrapper, 'stack-2'])}>
        {isLoading ? (
          <SkeletonComponent count={4} height="100px" />
        ) : error ? (
          <div className="error-message font-inter">
            <Translate value="bms.error_message" />
          </div>
        ) : (
          <>
            {/* Generic labelling information sections */}
            {data?.labellingInformation && data.labellingInformation.length > 0 && (
              <>
                {data.labellingInformation.map((section, i) => (
                  <Fragment key={i}>
                    {section.header && <h2>{section.header}</h2>}
                    {section.body ? <ReactMarkdown>{section.body}</ReactMarkdown> : null}
                  </Fragment>
                ))}
              </>
            )}

            {/* Client/Interface section */}
            {data?.client && (
              <div className={styles.clientSection}>
                <h2 className={styles.clientSectionHeading}>
                  <Translate value="bms.client.heading" />
                </h2>
                <p className={styles.clientSectionDescription}>
                  <Translate value="bms.client.description" />
                </p>

                {data.client.system && (
                  <div className={styles.metadataField}>
                    <span className={styles.metadataLabel}>
                      <Translate value="bms.labels.interface" />
                    </span>
                    <span>{data.client.system}</span>
                  </div>
                )}

                {data.client.labellingInformation?.map((section, i) => (
                  <Fragment key={i}>
                    {section.header && <h3>{section.header}</h3>}
                    {section.body && <ReactMarkdown>{section.body}</ReactMarkdown>}
                  </Fragment>
                ))}
              </div>
            )}

            {/* BMS System & Version */}
            {data?.version && (
              <div className={styles.metadataField}>
                <span className={styles.metadataLabel}>
                  <Translate value="bms.labels.version" />
                </span>
                <span>{data.version}</span>
              </div>
            )}

            {/* Grouped Modules */}
            {data?.modules && data.modules.length > 0 && (
              <div className={styles.modulesSection}>
                {Object.entries(groupedModules).map(([moduleType, modules]) => {
                  const typeKey = getModuleTypeKey(moduleType as BMSModuleType);
                  return (
                    <div key={moduleType} className={styles.moduleTypeSection}>
                      <h2 className={styles.moduleTypeHeading}>
                        <Translate value={`bms.modules.${typeKey}.heading`} />
                      </h2>
                      <p className={styles.moduleTypeDescription}>
                        <Translate value={`bms.modules.${typeKey}.description`} />
                      </p>

                      {modules.map((module, index) => (
                        <div key={index} className={styles.moduleCard}>
                          {/* Module Description */}
                          {module.description?.map((section, idx) => (
                            <Fragment key={idx}>
                              {section.header && <h4>{section.header}</h4>}
                              {section.body && <ReactMarkdown>{section.body}</ReactMarkdown>}
                            </Fragment>
                          ))}

                          {/* Module Metadata */}
                          <div className={styles.moduleMetadata}>
                            {module.classification && (
                              <div className={styles.metadataField}>
                                <img src={medicalDeviceSymbol} alt="MD" className={styles.symbolIcon} />
                                <div className={styles.metadataValue}>
                                  <span>
                                    <Translate value="bms.labels.name" /> {module.description[0]?.header}
                                  </span>
                                  <span>
                                    <Translate value="bms.labels.classification" /> {module.classification}
                                  </span>
                                  {module.version && (
                                    <span>
                                      <Translate value="bms.labels.version" /> {module.version}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {module.manufacturer && (
                              <div className={styles.metadataField}>
                                <img src={manufacturerSymbol} alt="Manufacturer" className={styles.symbolIcon} />
                                <ReactMarkdown>{module.manufacturer.replace(/\n/g, '  \n')}</ReactMarkdown>
                              </div>
                            )}

                            {module.udi && (
                              <div className={styles.metadataField}>
                                <img src={udiSymbol} alt="UDI" className={styles.symbolIcon} />
                                <span>{module.udi}</span>
                              </div>
                            )}

                            {module.udi && (
                              <div className={styles.metadataField}>
                                <img src={eifuSymbol} alt="eIFU" className={styles.symbolIcon} />
                                {module.eifu ? (
                                  <a
                                    href={module.eifu}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.metadataValueLink}
                                  >
                                    {' '}
                                    <span className={styles.metadataUrl}>{module.eifu}</span>
                                  </a>
                                ) : (
                                  <Translate value="bms.noeifu" />
                                )}
                              </div>
                            )}
                            {module.ceMarkIdentifier && (
                              <div className={styles.metadataField}>
                                <img src={ceSymbol} alt="CE" className={styles.ceSymbol} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Compatibility Warning */}
            {data?.compatibility === 'NOT_SUPPORTED' && (
              <div>
                <h2>
                  <Translate value="bms.compatibility_warning" />
                </h2>
              </div>
            )}

            {/* Footer */}
            {data?.version && (
              <div className={styles.footer}>
                <p>{data.version}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
